// ============================================================================
// Logistics dashboard API — self-contained axios calls. Uses the app's default
// axios instance (baseURL + JWT are set globally in src/index.tsx), so relative
// paths inherit auth. Thin: the OMS services own all the rules; the UI collects
// fields and shows the result.
// ============================================================================
import { message } from "antd";
import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";
import {
  BookingEscalation,
  DocPdfRef,
  ConfirmOpBody,
  FulfillmentType,
  LogisticsDocument,
  LogisticsOrderEdit,
  LogisticsOrderListResponse,
  LogisticsOrderRow,
  LogisticsSummary,
  PendingTasksResponse,
  ProductInfo,
  ProofListResponse,
  ReassignBody,
} from "../types";

export interface ListLogisticsParams {
  state?: string;
  provider_type?: string;
  status?: string;
  attention?: string;
  board_section?: string;
  board_lane?: string; // "first_mile" / "last_mile" — the half of the board being viewed
  sla?: string; // SlaState — "breached" / "due_soon" / "on_track"
  assigned_to?: string;
  // Inclusive target-dispatch-date range (YYYY-MM-DD). The board sends today by default.
  dispatch_from?: string;
  dispatch_to?: string;
  express?: boolean; // priority cut — express orders only
  // Intercity cut: only trucks whose cargo moves through a WH→WH transfer somewhere in its plan.
  // Answered server-side off the allotment's TRANSFER leg (shipment_type cannot tell you this).
  transfer?: boolean;
  include_cancelled?: boolean; // cancelled trucks are hidden by default; set to bring them back
  // Finished deliveries are dropped by default (the board is a work list). Set to keep them — what
  // a surface that reads completed work, not open work, wants. The response's `count` and folded
  // `summary` move with it, so they always describe what came back.
  include_delivered?: boolean;
  // Only trucks that are ON a Loginext trip (a named trip their vehicle was allotted against).
  // Narrows results, `count` and the folded `summary` together, so all three agree.
  has_trip?: boolean;
  unassigned?: boolean; // only trucks with no owner yet (the "Unassigned" Owner-filter option)
  warehouse_id?: number; // scope to trucks one MD warehouse dispatches from / receives into
  city?: string; // the order's DELIVERY city — the city team's own trucks
  // Drop outstation clients (the Outstation Client tab's rows) — Last Mile sets it. Ignored by the
  // server while searching, like the lane.
  exclude_outstation?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

// Scope params the attention strip honours (mirror of the list's SQL-filterable scopes).
export interface SummaryParams {
  state?: string;
  provider_type?: string;
  assigned_to?: string;
  dispatch_from?: string;
  dispatch_to?: string;
  express?: boolean;
  transfer?: boolean;
  include_cancelled?: boolean;
  include_delivered?: boolean; // count finished deliveries too — mirrors the list's own param
  unassigned?: boolean;
  warehouse_id?: number;
  city?: string;
  exclude_outstation?: boolean; // mirrors the list's own param, so the strip counts match the rows
  board_lane?: string; // scope the strip to the half of the board being viewed
}

export async function listLogisticsOrders(
  params: ListLogisticsParams = {}
): Promise<LogisticsOrderListResponse | undefined> {
  try {
    return await axios
      .get("/oms/logistics/", { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading logistics orders");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// The logistics-order CARGO detail: ONLY the allocations riding this truck (lead leg + riders) and,
// per allocation, its item lines. Drives the item-focused modal — everything the board row already
// shows is left out. undefined on failure; a 404 resolves quietly so the modal shows a not-found
// state rather than a toast.
export interface LogisticsCargoItem {
  id: number;
  sales_order_line_id: number | null;
  product: ProductInfo | null;
  qty: string | number;
  unit: string | null;
}

export interface LogisticsCargoAllocation {
  id: number;
  po_number: string | null;
  supplier_name: string | null;
  shipment_type: string | null;
  // The ORDER this PO belongs to. A consolidated truck carries POs from several orders, and the
  // contents modal groups on this pair before listing POs. Optional: when absent, the modal resolves
  // it per PO via `lookupPoOrderContexts` — never from the booking that happened to be clicked.
  lead_id?: string | null;
  client_name?: string | null;
  items: LogisticsCargoItem[];
  // This PO's own documents with a PDF — its purchase order and delivery challan. Per PO, not per
  // truck: the manifest that rides with the driver is assembled PO by PO, and the operator printing
  // it has no order detail page open. Empty until a doc has actually reached Zoho.
  documents?: DocPdfRef[];
  // Present only for a TRANSFER truck: the leg this PO rides, the transfer order it is on, and
  // whether it can be moved to another one. Server-declared (like a row's available_actions) — the
  // draft rule lives in the OMS, never here.
  transfer_stage_id: number | null;
  transfer_order_number: string | null;
  can_move: boolean;
  move_blocked_reason: string | null;
  // Present only for a customer SHIPMENT truck: the leg this PO rides and whether it can be split
  // onto a truck of its own. Server-declared, like the move fields.
  shipment_stage_id: number | null;
  can_ship_separately: boolean;
  ship_separately_blocked_reason: string | null;
}

// Take a shipment allotment off its consolidated truck and book it on its own truck.
export async function shipSeparately(stageId: number): Promise<boolean> {
  try {
    await axios.post(`/oms/stages/${stageId}/ship-separately/`);
    message.success("Moved onto a separate truck");
    return true;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    if (detail) message.error(detail);
    else errorActions(error, "Could not ship this PO separately");
    return false;
  }
}

// One transfer order as the move dialog lists it — identified by what is ON it, not by an id.
export interface TransferOrderSummary {
  document_id: number;
  transfer_order_number: string;
  zoho_transfer_order_id: string | null;
  in_zoho: boolean;
  po_numbers: string[];
}

export interface TransferMoveTargets {
  stage_id: number;
  can_move: boolean;
  blocked_reason: string | null;
  current: TransferOrderSummary | null;
  targets: TransferOrderSummary[];
}

// Where this allotment can move. An empty `targets` is a normal answer: nothing else is open on the
// lane, so the only move available is onto a new transfer order.
export async function getTransferMoveTargets(
  stageId: number
): Promise<TransferMoveTargets | undefined> {
  try {
    const res = await axios.get(`/oms/stages/${stageId}/move-targets/`);
    return res?.data as TransferMoveTargets;
  } catch (error: any) {
    errorActions(error, "Could not load the transfer orders this PO can move to");
    return undefined;
  }
}

// Move the allotment onto `targetDocumentId`, or onto a BRAND-NEW transfer order when it is null.
// The old order is amended down and the goods come off its truck; the leg re-books itself.
export async function moveTransfer(
  stageId: number,
  targetDocumentId: number | null
): Promise<boolean> {
  try {
    await axios.post(`/oms/stages/${stageId}/move-transfer/`, {
      target_document_id: targetDocumentId,
    });
    message.success(
      targetDocumentId
        ? "Moved to the selected transfer order"
        : "Moved onto a new transfer order"
    );
    return true;
  } catch (error: any) {
    // The OMS refuses with an operator-facing reason (the goods are already moving, the target
    // closed under us) — show THAT, not a generic failure.
    const detail = error?.response?.data?.detail;
    if (detail) message.error(detail);
    else errorActions(error, "Could not move this PO to another transfer order");
    return false;
  }
}

export interface LogisticsOrderDetail {
  id: number;
  allocations: LogisticsCargoAllocation[];
}

// The CONTENTS of one allotment — same shape (and same server serializer) as a booking's own
// `allocations` block, so the board's contents modal can render it unchanged.
//
// Exists for the sections whose rows have NO booking: Missing Orders and Awaiting Payment. Every
// other section opens that modal with `getLogisticsOrder(docId)`, which needs a truck, and the whole
// point of those rows is that no truck was ever created. The per-truck actions come back false from
// the server, which is correct — there is nothing to move the cargo off.
export async function getAllocationCargo(
  allocationId: number
): Promise<{ allocations: LogisticsCargoAllocation[] } | undefined> {
  try {
    const res = await axios.get(`/oms/allocations/${allocationId}/cargo/`);
    return res?.data as { allocations: LogisticsCargoAllocation[] };
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      errorActions(error, "Could not load this order's contents");
    }
    return undefined;
  }
}

// One booking as a board row — what the "View all details" drawer renders, for surfaces (the
// Returns tab) that list stages rather than board rows.
export async function getLogisticsRow(docId: number): Promise<LogisticsOrderRow | undefined> {
  try {
    const res = await axios.get(`/oms/logistics/${docId}/row/`);
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    errorActions(error, "Could not load this booking");
    return undefined;
  }
}

export async function getLogisticsOrder(
  docId: number
): Promise<LogisticsOrderDetail | undefined> {
  try {
    const res = await axios.get(`/oms/logistics/${docId}/`);
    return res?.data as LogisticsOrderDetail;
  } catch (error: any) {
    if (error?.response?.status !== 404) {
      errorActions(error, "Could not load this logistics order");
    }
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// PO → ORDER context. A consolidated truck carries POs from MANY orders (booking
// 3783 rides 37 POs across 21 leads), and the cargo serializer above returns only
// the PO itself — no lead / client. Attributing those POs to the BOOKING's own
// lead is how a 1-PO order ends up looking like a 37-PO one, so the contents modal
// resolves each PO's real order here instead of assuming.
//
// The PO list endpoint already searches by PO number and returns the order context,
// so this is a concurrency-capped fan-out over it — the same shape as
// `listProofTypesForDocs`. Results are cached for the session: the same trucks get
// reopened all day and a PO never changes the order it belongs to.
// ---------------------------------------------------------------------------
export interface PoOrderContext {
  po_number: string;
  lead_id: string | null;
  client_name: string | null;
  sales_order_id: number | null;
}

const poContextCache = new Map<string, PoOrderContext | null>();

const normalisePo = (po: string): string => po.trim().toUpperCase();

// Resolves what we can and stays quiet about the rest: one unresolved PO must not raise a toast
// (37 of them would bury the screen), and an unresolved PO is shown as its own un-attributed group
// rather than being filed under someone else's order.
export async function lookupPoOrderContexts(
  poNumbers: Array<string | null | undefined>,
  concurrency = 6
): Promise<Record<string, PoOrderContext>> {
  const out: Record<string, PoOrderContext> = {};
  const queue: string[] = [];

  const unique = Array.from(new Set((poNumbers ?? []).filter(Boolean) as string[]));
  for (const raw of unique) {
    const cached = poContextCache.get(normalisePo(raw));
    if (cached === undefined) queue.push(raw);
    else if (cached) out[raw] = cached;
  }

  const worker = async () => {
    while (queue.length) {
      const po = queue.shift()!;
      try {
        const res = await axios.get("/oms/purchase-orders/", {
          // `all` so a completed / cancelled allotment still resolves — the default tab is a cut.
          params: { search: po, tab: "all", limit: 5 },
        });
        const rows: any[] = res?.data?.results ?? [];
        // The search is a CONTAINS match, so take the row whose PO number is exactly the one asked
        // for — never a longer PO number that merely contains it.
        const exact = rows.find(
          (r) => r?.po_number && normalisePo(r.po_number) === normalisePo(po)
        );
        const hit: PoOrderContext | null = exact
          ? {
              po_number: po,
              lead_id: exact.lead_id ?? null,
              client_name: exact.client_name ?? null,
              sales_order_id: exact.sales_order_id ?? null,
            }
          : null;
        // Cache the miss too — it is an answer, not a failure, and re-asking costs a request.
        poContextCache.set(normalisePo(po), hit);
        if (hit) out[po] = hit;
      } catch {
        // A failed request is NOT cached: the next open should try again.
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, queue.length) }, worker)
  );
  return out;
}

// One selectable owner for the board's Owner dropdown. `value` is the user's id — stored on the
// booking's `assigned_to` and matched by the board's Owner filter; `label` is the name shown.
export interface TeamMember {
  value: string;
  label: string;
  email: string | null;
  role: string | null; // UserPermission.permission_name, e.g. "delivery"
}

// One MD warehouse for the board's warehouse filter.
export interface WarehouseOption {
  id: number;
  label: string;
  city: string | null;
}

// MD's own warehouses, for the board's warehouse filter. Silent on failure (the filter just shows
// no options rather than raising a toast on the board load).
export async function listWarehouses(): Promise<WarehouseOption[]> {
  try {
    const res = await axios.get("/oms/logistics/warehouses/");
    return (res?.data?.results as WarehouseOption[]) ?? [];
  } catch {
    return [];
  }
}

// --- A trip's planned route ---------------------------------------------------------------------
// Read back from the DELIVERYPLANNING events OMS already stores per stop (no carrier call) — see
// oms/logistics/services/trip_map.py. Stops arrive in the order the driver runs them.
export interface TripStop {
  seq: number | null; // LogiNext's own `deliveryOrder` — NOT 1..n; it skips. Sorted by, never indexed by.
  type: "pickup" | "deliver" | string | null;
  // THE STOP ITSELF — the customer on a DELIVER row, the vendor on a PICKUP row.
  lat: number;
  lng: number;
  // The other end of the shipment — never a sequenced stop. `other_is_warehouse` says whether it is
  // one of OUR docks (drawn "W") or another party's site (drawn "P"); neither carries a number.
  other_lat: number | null;
  other_lng: number | null;
  other_is_warehouse: boolean;
  // True when LogiNext never sent us a planning row for this shipment — it is placed from the
  // booking's own endpoints and carries no position in the run. Drawn without a number.
  unsequenced?: boolean;
  origin_lat: number | null;
  origin_lng: number | null;
  dest_lat: number | null;
  dest_lng: number | null;
  order_no: string | null; // LogiNext's, with its `_M<n>` leg suffix
  provider_ref: string | null; // …and ours, leg-less — what matches a row on the board
  booking_id: number | null;
  client_name: string | null;
  client_city: string | null;
  window_start: string | null;
  window_end: string | null;
  eta_start: string | null;
  eta_end: string | null;
  weight_kg: number | null;
  volume: number | null;
  // What has happened at THIS end of the shipment, off the events we hold. `done_at` is ISO/UTC.
  status?: "done" | "failed" | "pending";
  done_at?: string | null;
  // Whether this stop is one of our own docks — so a list can say "Our warehouse".
  at_warehouse?: boolean;
}

export interface PlannedTrip {
  trip_name: string;
  driver: string | null;
  phone: string | null;
  planned_at: string | null;
  stops: TripStop[];
  live_location: { lat: number; lng: number } | null; // null until the live-position pass lands
}

export async function getPlannedTrip(tripName: string): Promise<PlannedTrip | undefined> {
  try {
    const res = await axios.get("/oms/logistics/trip-map/", { params: { trip_name: tripName } });
    return res?.data as PlannedTrip;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not load this trip's route");
    return undefined;
  }
}

// --- Live delivery ETA -------------------------------------------------------------------------
// Loginext's live ETA for the bookings on screen, asked for SEPARATELY from the board's list.
// Their shipment endpoint takes one reference at a time and has no bulk form, so folding this into
// the list would mean one third-party round trip per row inside the request that draws the board.
// Asking afterwards means a slow Loginext costs an empty column, not an empty page.
//
// A booking MISSING from the map was never asked about (not a Loginext truck, no shipment
// reference, or already delivered). A booking mapped to null was asked, and Loginext had nothing.
export interface LiveEtaResult {
  results: Record<string, string | null>;
  // TRUE when the request itself failed. Reported rather than swallowed: an empty `results` from a
  // failed call looks identical to "LogiNext had no ETA", and a column that says the second when
  // the first happened is worse than one that says nothing. No toast — this is a background
  // enrichment and a toast would fire on every refresh of a board whose carrier is having a bad
  // day — but the caller can word its tooltip honestly, and the console carries the detail.
  failed: boolean;
}

export async function getLiveEtas(bookingIds: number[]): Promise<LiveEtaResult> {
  if (!bookingIds.length) return { results: {}, failed: false };
  try {
    const res = await axios.post("/oms/logistics/live-eta/", { booking_ids: bookingIds });
    return { results: (res?.data?.results as Record<string, string | null>) || {}, failed: false };
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.warn("Live ETA lookup failed", error?.response?.status, error?.response?.data ?? error);
    return { results: {}, failed: true };
  }
}

// LogiNext's embeddable TRACKING page for one booking — their only documented iFrame
// (`track/#/order?ordno=&aid=`). Minted server-side because the iFrame token lives in settings and
// expires roughly fortnightly. `undefined` when no token is configured, or the call failed.
export async function getLoginextTrackingEmbed(
  orderNo: string
): Promise<string | undefined> {
  try {
    const res = await axios.get("/oms/logistics/tracking-embed/", {
      params: { order_no: orderNo },
    });
    return (res?.data?.url as string) || undefined;
  } catch (error: any) {
    message.error(
      error?.response?.data?.detail || "Could not open LogiNext tracking for this shipment"
    );
    return undefined;
  }
}

// The ops team for an Owner dropdown. `role` (one or more UserPermission.permission_name values,
// comma-separated) narrows the roster to those roles — omit it for every active member, whatever
// their role, which is what this board's Owner dropdown wants. Silent on failure — the dropdown
// just shows no options rather than raising a toast on the board load.
export async function listTeamMembers(params: { role?: string } = {}): Promise<TeamMember[]> {
  try {
    const res = await axios.get("/oms/logistics/team-members/", { params });
    return (res?.data?.results as TeamMember[]) ?? [];
  } catch {
    return [];
  }
}

// The board's attention strip — counts per attention bucket + board section for the current scope.
// Silent on failure (the strip just shows nothing rather than raising a toast on every poll).
export async function getLogisticsSummary(
  params: SummaryParams = {}
): Promise<LogisticsSummary | undefined> {
  try {
    const res = await axios.get("/oms/logistics/summary/", { params });
    return res?.data as LogisticsSummary;
  } catch {
    return undefined;
  }
}

// Cancel a stray / duplicate / no-longer-needed truck. Live bookings hand the physical cancel to the
// drain (carrier cancel, or a manual 'confirm cancellation' task); a never-booked one cancels at once.
export async function cancelBooking(
  docId: number
): Promise<LogisticsOrderRow | undefined> {
  try {
    const res = await axios.post(`/oms/logistics/${docId}/cancel/`);
    message.success("Booking cancelled");
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Cancellation failed");
    return undefined;
  }
}

// Retry a booking's failed carrier op (the drain re-picks it next pass). For a HELD / failed booking.
export async function requeueBooking(
  docId: number
): Promise<LogisticsOrderRow | undefined> {
  try {
    const res = await axios.post(`/oms/logistics/${docId}/requeue/`);
    message.success("Booking requeued — the carrier call will retry");
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Requeue failed");
    return undefined;
  }
}

// Mark a delivery as attempted-and-failed on one booking. Only valid on a customer-delivery leg
// (last-mile shipment / P2P dropship); the backend 400s a non-delivery leg or an already
// delivered/cancelled one, with a {detail} message the caller surfaces. On success the booking's
// delivery_status becomes `attempted` and a reattempt opens up with no in-transit warning. `reason`
// is optional (passed through to the delivery team). Resolves to the updated row, or undefined on
// failure (the {detail} message is surfaced via antd).
export async function markDeliveryAttempted(
  docId: number,
  reason?: string
): Promise<LogisticsOrderRow | undefined> {
  try {
    const res = await axios.post(
      `/oms/logistics/${docId}/mark-delivery-attempted/`,
      { reason: reason ?? "" }
    );
    message.success("Delivery marked attempted");
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Could not mark delivery attempted");
    return undefined;
  }
}

// Hand-edit one booking's operational fields. Resolves to the updated row, or undefined on failure
// (the endpoint's {detail} message is surfaced via antd).
export async function updateLogisticsOrder(
  docId: number,
  body: LogisticsOrderEdit
): Promise<LogisticsOrderRow | undefined> {
  try {
    const res = await axios.patch(`/oms/logistics/${docId}/`, body);
    message.success("Logistics order updated");
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Update failed");
    return undefined;
  }
}

// The server authors action hrefs via Django reverse(), so they include the API mount prefix
// (e.g. "/apiV1/oms/…"). Axios' baseURL already ends in that same prefix, so posting the raw href
// would double it ("/apiV1/apiV1/…"). Strip the baseURL's path prefix so the href combines cleanly —
// mirrors how every other call in this app passes prefix-less relative paths.
const API_BASE =
  process.env.REACT_APP_API_URL || "https://api-dev2.materialdepot.in/apiV1";

export function toAxiosPath(href: string): string {
  try {
    const basePath = new URL(API_BASE).pathname.replace(/\/+$/, ""); // e.g. "/apiV1"
    if (basePath && (href === basePath || href.startsWith(basePath + "/"))) {
      return href.slice(basePath.length) || "/";
    }
  } catch {
    /* API_BASE isn't an absolute URL — fall through and use the href as-is */
  }
  return href;
}

// Drive a manual status transition on one booking (mark in-transit / delivered). The endpoint + verb
// come from the booking's `available_actions[].href` (server-authored), so the UI never hard-codes the
// route. `body` is optional — mark-in-transit accepts {vehicle_number, driver_name, driver_number}.
// Resolves to the updated row, or undefined on failure (the {detail} message is surfaced via antd).
export async function runLogisticsAction(
  href: string,
  label: string,
  body?: Record<string, any>
): Promise<LogisticsOrderRow | undefined> {
  try {
    const res = await axios.post(toAxiosPath(href), body ?? {});
    message.success(`${label} ✓`);
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || `${label} failed`);
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Manual providers (Porter / intercity): the pending-task inbox + confirm + reassign.
// A manual booking/cancel/modify is dispatched by the backend as a task the ops team
// actions on the partner's app and then confirms here — the same outbox as automated TMS.
// ---------------------------------------------------------------------------

// The pending-task inbox: manual provider ops awaiting the ops team's confirmation. Optional `op`
// filter (book / cancel / update) and `express` (priority cut).
export async function listPendingTasks(
  opts: { op?: string; express?: boolean; limit?: number; offset?: number } = {}
): Promise<PendingTasksResponse | undefined> {
  const params: Record<string, string> = {};
  if (opts.op) params.op = opts.op;
  if (opts.express) params.express = "true";
  if (opts.limit) params.limit = String(opts.limit);
  if (opts.offset) params.offset = String(opts.offset);
  try {
    return await axios
      .get("/oms/logistics/pending-tasks/", { params })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading pending tasks");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

// Confirm a manual op the partner has actioned. A BOOK confirm carries the ref/tracking (+ trip);
// a CANCEL / UPDATE confirm is a bare acknowledgement. Resolves to the updated booking, or undefined.
export async function confirmManualOp(
  opId: number,
  body: ConfirmOpBody
): Promise<LogisticsDocument | undefined> {
  try {
    const res = await axios.post(`/oms/logistics/ops/${opId}/confirm/`, body);
    message.success("Confirmed ✓");
    return res?.data as LogisticsDocument;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Confirmation failed");
    return undefined;
  }
}

// Move a booking to a different provider by hand (override the router). Returns the NEW booking.
export async function reassignProvider(
  docId: number,
  body: ReassignBody
): Promise<LogisticsDocument | undefined> {
  try {
    const res = await axios.post(`/oms/logistics/${docId}/reassign/`, body);
    message.success("Re-assigned ✓");
    return res?.data as LogisticsDocument;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Re-assignment failed");
    return undefined;
  }
}

// Every field is mandatory — ops allots the vendor, truck and driver together, so a trip assignment
// with half of them filled in isn't a state worth recording. The server rejects a partial body too.
export interface AssignTripVehicleBody {
  trip_name: string;
  vehicle_number: string;
  driver_name: string;
  driver_number: string;
  // The fleet partner running the trip (not the material vendor on the order). The booking has no
  // column for it — the server keeps it on the assignment event's payload as an audit trail.
  transport_vendor_name: string;
}

export interface AssignTripVehicleResult {
  trip_name: string;
  vehicle_number: string;
  transport_vendor_name?: string | null;
  updated_count: number;
  logistics_documents: LogisticsOrderRow[];
}

// ---------------------------------------------------------------------------
// Fulfilment proofs (POP / POD / LR). List + upload + soft-delete for one booking.
// LoginExt's ePOD arrives via a server-side webhook — the FE only ever
// lists / uploads / deletes, never touches that callback.
// ---------------------------------------------------------------------------

// A booking's live (non-deleted) proofs, newest first. undefined on failure;
// 404 means the booking itself is gone. Pass `silent` when fetching many rows'
// proofs for the table capsules — one failure there must not raise 50 toasts.
export async function listProofs(
  docId: number,
  opts: { silent?: boolean } = {}
): Promise<ProofListResponse | undefined> {
  try {
    const res = await axios.get(`/oms/logistics/${docId}/proofs/`);
    return res?.data as ProofListResponse;
  } catch (error: any) {
    if (!opts.silent) errorActions(error, "Could not load fulfilment proofs");
    return undefined;
  }
}

// Which proof categories each booking has, for the dashboard's row capsules.
// Fetched client-side (the list row carries no proof summary) with a small
// concurrency cap so a page of 50 rows doesn't open 50 sockets at once.
export async function listProofTypesForDocs(
  docIds: number[],
  concurrency = 6
): Promise<Record<number, FulfillmentType[]>> {
  const out: Record<number, FulfillmentType[]> = {};
  const queue = [...docIds];
  const worker = async () => {
    while (queue.length) {
      const id = queue.shift()!;
      const res = await listProofs(id, { silent: true });
      out[id] = res
        ? Array.from(new Set(res.results.map((p) => p.proof_type)))
        : [];
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, docIds.length) }, worker)
  );
  return out;
}

// Upload distinguishes the failure modes the UI must react to differently:
//   400 → validation (missing/invalid type or files) — surfaced field-by-field
//   502 → the R2 storage write failed — a plain "retry" is the right advice
// so it resolves to a discriminated result rather than a bare undefined.
export type UploadProofsResult =
  | { ok: true; data: ProofListResponse }
  | {
      ok: false;
      status?: number;
      // DRF-style {field: [msg, …]} pulled out of a 400 body, when present.
      fieldErrors?: Record<string, string[]>;
      message: string; // a human sentence safe to show as-is
    };

export async function uploadProofs(
  docId: number,
  fulfillmentType: FulfillmentType,
  files: File[]
): Promise<UploadProofsResult> {
  const form = new FormData();
  form.append("fulfillment_type", fulfillmentType);
  // Repeat the field once per file — this is how DRF reads a multi-file field.
  files.forEach((f) => form.append("proof_files", f, f.name));
  try {
    // Leave Content-Type unset: axios stamps the multipart boundary itself.
    const res = await axios.post(`/oms/logistics/${docId}/proofs/`, form);
    return { ok: true, data: res?.data as ProofListResponse };
  } catch (error: any) {
    const status: number | undefined = error?.response?.status;
    const data = error?.response?.data;
    if (status === 502) {
      return {
        ok: false,
        status,
        message: "Storage upload failed — please retry.",
      };
    }
    if (status === 404) {
      return { ok: false, status, message: "This booking no longer exists." };
    }
    if (status === 400 && data && typeof data === "object") {
      const fieldErrors: Record<string, string[]> = {};
      for (const [key, val] of Object.entries(data)) {
        if (key === "detail") continue;
        fieldErrors[key] = Array.isArray(val) ? val.map(String) : [String(val)];
      }
      return {
        ok: false,
        status,
        fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined,
        message: data?.detail || "Please check the files and try again.",
      };
    }
    return { ok: false, status, message: data?.detail || "Upload failed." };
  }
}

// Soft-delete one proof. The list endpoint already hides deleted rows, so the
// caller just re-fetches on success. Returns whether it worked.
export async function deleteProof(proofId: number): Promise<boolean> {
  try {
    await axios.post(`/oms/logistics/proofs/${proofId}/delete/`);
    return true;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Could not delete this proof");
    return false;
  }
}

// Pull a Loginext trip's orders and stamp the driver + vehicle across every booking on it.
export async function assignTripVehicle(
  body: AssignTripVehicleBody
): Promise<AssignTripVehicleResult | undefined> {
  try {
    const res = await axios.post("/oms/logistics/assign-trip-vehicle/", body);
    const updated = res?.data?.updated_count ?? 0;
    // A mistyped trip name matches nothing and still returns 200 — reporting that as a green
    // success is how a driver silently never gets assigned. Zero is a warning, not a win.
    if (updated === 0) {
      message.warning(
        `No bookings found on trip "${body.trip_name}" — nothing was assigned. Check the trip ID.`
      );
    } else {
      message.success(
        `Assigned ${body.vehicle_number} to ${updated} booking${updated === 1 ? "" : "s"}`
      );
    }
    return res?.data as AssignTripVehicleResult;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Trip assignment failed");
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Bulk auto-assign the board's Owner for a hand-picked set of bookings (the board's
// row selection). The server runs the same routing table as create-time assignment;
// a booking that already has an owner is left untouched (reported `already_assigned`),
// and a leg the router can't place (uncovered zone / unlocatable dropship vendor) is
// reported `no_route`. Only ever fills a blank owner.
// ---------------------------------------------------------------------------
export type AutoAssignSkipReason = "already_assigned" | "no_route" | "not_found";

export interface AutoAssignOwnersResult {
  dry_run?: boolean;
  assigned_count: number;
  assigned_ids: number[];
  skipped_count: number;
  skipped: { id: number; reason: AutoAssignSkipReason }[];
}

export async function autoAssignOwnersByIds(
  docIds: number[]
): Promise<AutoAssignOwnersResult | undefined> {
  try {
    const res = await axios.post("/oms/logistics/auto-assign-owners/", { document_ids: docIds });
    const data = res?.data as AutoAssignOwnersResult;
    const n = data?.assigned_count ?? 0;
    const already = data?.skipped?.filter((s) => s.reason === "already_assigned").length ?? 0;
    const noRoute = data?.skipped?.filter((s) => s.reason === "no_route").length ?? 0;
    const notFound = data?.skipped?.filter((s) => s.reason === "not_found").length ?? 0;
    const tail =
      (already ? ` · ${already} already had an owner` : "") +
      (noRoute ? ` · ${noRoute} had no routing rule` : "") +
      (notFound ? ` · ${notFound} not found` : "");
    if (n === 0) {
      message.warning(`No owners assigned${tail || " — nothing to do"}`);
    } else {
      message.success(`Assigned owners to ${n} booking${n === 1 ? "" : "s"}${tail}`);
    }
    return data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Auto-assign failed");
    return undefined;
  }
}

// Bulk manual assign — set ONE chosen owner on the selected bookings. Overrides an existing owner
// (an explicit hand pick), so already-assigned rows are fair game too.
export async function assignOwnerByIds(
  docIds: number[],
  assigneeId: string
): Promise<AutoAssignOwnersResult | undefined> {
  try {
    const res = await axios.post("/oms/logistics/assign-owner/", {
      document_ids: docIds,
      assignee_id: assigneeId,
    });
    const data = res?.data as AutoAssignOwnersResult;
    const n = data?.assigned_count ?? 0;
    if (n === 0) message.warning("Nothing was assigned");
    else message.success(`Assigned ${n} booking${n === 1 ? "" : "s"} to the selected owner`);
    return data;
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    message.error(detail || "Assignment failed");
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Warehouse work queues — pending picklist / pending GRN. One shape, two kinds:
// what a warehouse owes right now, ordered by SLA due date (oldest first, which
// is the order the desk should work it).
// ---------------------------------------------------------------------------
export type PendingQueueKind = "picklist" | "grn";

// Who physically moves the goods on an inbound leg. `self_pickup` — we collect
// from the vendor; `supplier_delivered` — the vendor brings it to our dock, so
// there is no truck of ours to track and a follow-up means calling the VENDOR.
// Null on a picklist row, which is not an inbound leg at all.
export type PendingFulfilment = "self_pickup" | "supplier_delivered";

export interface PendingStageRow {
  id: number;
  operation_type: string;
  status: string;
  issue_state: string;
  due_at: string | null;
  sales_order_id: number | null;
  allocation_id: number | null;
  lead_id: string | null;
  po_number: string | null;
  client_name: string | null;
  supplier_name: string | null;
  warehouse_id: number | null;
  warehouse_name: string | null;
  // The allocation's own last-mile ship-from and GRN dock — carried on every row on both queues
  // so the desk can scope by either side, independent of which endpoint of this leg is MD.
  // They differ exactly when a transfer is in the chain, which is what the Destination column reads.
  fulfilment_warehouse_id: number | null;
  fulfilment_warehouse_name: string | null;
  receiving_warehouse_id: number | null;
  receiving_warehouse_name: string | null;
  // The allotment still has a CITY to cross after this leg (a line-haul is ahead of
  // it), so a slipped inbound eats the customer's promise twice over.
  is_intercity: boolean;
  fulfilment: PendingFulfilment | null;
  is_supplier_delivered: boolean;
  promised_availability_date: string | null; // the date the VENDOR promised the goods
  days_overdue: number | null;               // whole days past that promise, else null
  is_express: boolean;
  is_unloading: boolean; // order needs unloading labour at the customer site (like is_express)
  qty: string | number;
  item_count: number;
  // Total cargo weight for the leg — shown under the PO like on the boards. Null when unmapped.
  cargo_weight_kg?: number | null;
  error_message?: string | null;
  hold_reason?: string | null;
  // The Zoho BOOKS deep-link for this row's own document — the picklist on the pick queue,
  // the in-transit GRN on the inbound one. Null until the document exists in Zoho.
  zoho_url: string | null;
  zoho_number: string | null;
  // What somebody flagged on this leg and nobody has resolved. Same shape (and same append-only
  // log) as a booking's escalation on the board — but an escalated queue row STAYS in its queue:
  // the pick is still owed, so hiding it would lose the work.
  escalation: BookingEscalation | null;
}

export interface PendingQueueResponse {
  count: number;
  express_count: number;
  supplier_delivered_count: number;
  intercity_count: number;
  escalated_count: number;
  warehouses: Array<{ id: number; label: string; city: string | null }>;
  results: PendingStageRow[];
}

export async function fetchPendingQueue(params: {
  kind: PendingQueueKind;
  warehouse_id?: number;
  fulfilment_warehouse_id?: number;
  receiving_warehouse_id?: number;
  express?: boolean;
  search?: string;
  fulfilment?: PendingFulfilment;
  intercity?: boolean;
  escalated?: boolean;
}): Promise<PendingQueueResponse | undefined> {
  try {
    const res = await axios.get("/oms/stages/pending/", {
      params: {
        kind: params.kind,
        warehouse_id: params.warehouse_id || undefined,
        fulfilment_warehouse_id: params.fulfilment_warehouse_id || undefined,
        receiving_warehouse_id: params.receiving_warehouse_id || undefined,
        express: params.express ? "true" : undefined,
        search: params.search || undefined,
        // GRN only — the backend ignores it on the picklist queue.
        fulfilment: params.fulfilment || undefined,
        intercity: params.intercity ? "true" : undefined,
        escalated: params.escalated ? "true" : undefined,
        limit: 200,
      },
    });
    return res?.data as PendingQueueResponse;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not load the queue");
    return undefined;
  }
}

export async function getLoginextTrackingUrl(
  orderNo: string
): Promise<string | undefined> {
  try {
    const res = await axios.get("/oms/logistics/loginext-tracking-url/", {
      params: { order_no: orderNo },
    });
    return res?.data?.url as string | undefined;
  } catch (error: any) {
    errorActions(error, "Could not open this order in LogiNext");
    return undefined;
  }
}

// ── Operations issues ───────────────────────────────────────────────────────
// Flagging a problem on a booking (the port of legacy delivery-v2's escalation drawer). This
// notifies the responsible teams on WhatsApp and opens a CRM deal; it changes nothing about the
// order itself — the order-mutating actions live on the order detail page.

export interface IssueCategory {
  category: string; // routing key ("procurement" / "delivery" / "cs")
  label: string;
  issues: string[];
}

export interface ExistingTicket {
  deal_id: string;
  deal_url: string;
  title: string;
  stage: string | null;
  closed: boolean;
  rca: string;
  resolution: string;
  reasons: string;
  same_issue: boolean;
}

// Tickets already on this order (open + the latest closed), for the "is it one of these?" check.
// Informational: an empty list on failure, never blocks raising a new escalation.
export async function fetchExistingTickets(params: {
  lead_id?: string | null;
  po_number?: string | null;
  issue_type?: string | null;
}): Promise<ExistingTicket[]> {
  const query = Object.fromEntries(Object.entries(params).filter(([, v]) => !!v));
  if (!query.lead_id && !query.po_number) return [];
  try {
    const res = await axios.get("/crm/escalation/existing/", { params: query });
    return (res?.data?.tickets ?? []) as ExistingTicket[];
  } catch {
    return [];
  }
}

// The pickable issue types, grouped by category. Served by the backend from the same config it
// routes on, so the drawer can never offer an issue the server would reject.
export async function listIssueTypes(): Promise<IssueCategory[]> {
  try {
    const res = await axios.get("/oms/logistics/issue-types/");
    return (res?.data?.categories ?? []) as IssueCategory[];
  } catch {
    message.error("Could not load the issue types");
    return [];
  }
}

// Raise the issue. A 502 means it WAS recorded but at least one WhatsApp group did not get it —
// surfaced as a warning naming the groups, because "escalation raised" that nobody received is the
// one outcome an operator must not walk away believing.
export async function raiseLogisticsIssue(
  docId: number,
  body: { issue_type: string; remarks?: string }
): Promise<boolean> {
  try {
    await axios.post(`/oms/logistics/${docId}/raise-issue/`, body);
    message.success("Escalation raised ✓");
    return true;
  } catch (error: any) {
    const data = error?.response?.data;
    if (error?.response?.status === 502) {
      message.warning(
        data?.detail || "Recorded, but the WhatsApp message did not reach every team",
        6
      );
      return true;
    }
    message.error(data?.detail || "Could not raise the escalation");
    return false;
  }
}

// The same two calls for a WAREHOUSE QUEUE row (pending picklist / pending GRN), keyed on the
// STAGE. Not an alternative spelling of the booking pair: a PICK leg books no truck, and neither
// does a vendor-delivered inbound, so `/logistics/<id>/…` cannot reach the legs these queues are
// made of. Both resolve to the row's own escalation (null once resolved), which the panel merges
// into the row it already has rather than refetching the whole queue.
export async function raiseStageIssue(
  stageId: number,
  body: { issue_type: string; remarks?: string }
): Promise<{ ok: boolean; escalation: BookingEscalation | null }> {
  try {
    const res = await axios.post(`/oms/stages/${stageId}/raise-issue/`, body);
    message.success("Escalation raised ✓");
    return { ok: true, escalation: (res?.data?.escalation ?? null) as BookingEscalation | null };
  } catch (error: any) {
    const data = error?.response?.data;
    // 502: recorded, but a WhatsApp group did not get it. The escalation IS open, so the row must
    // still take it — "raised" that nobody received is the one thing an operator must not believe.
    if (error?.response?.status === 502) {
      message.warning(
        data?.detail || "Recorded, but the WhatsApp message did not reach every team",
        6
      );
      return { ok: true, escalation: (data?.escalation ?? null) as BookingEscalation | null };
    }
    message.error(data?.detail || "Could not raise the escalation");
    return { ok: false, escalation: null };
  }
}

export async function resolveStageIssue(
  stageId: number,
  remarks?: string
): Promise<boolean> {
  try {
    await axios.post(`/oms/stages/${stageId}/resolve-issue/`, {
      remarks: remarks?.trim() || undefined,
    });
    message.success("Escalation resolved ✓");
    return true;
  } catch (error: any) {
    message.error(
      error?.response?.data?.detail || "Could not resolve the escalation"
    );
    return false;
  }
}

// Close the escalation out — the way a truck leaves the board's escalation section and goes back to
// its working one. Appends to the same log the raise went into (nothing is erased), and sends no
// WhatsApp message: the raise was the alert. Returns the updated row so the board can merge it.
export async function resolveLogisticsIssue(
  docId: number,
  remarks?: string
): Promise<LogisticsOrderRow | null> {
  try {
    const res = await axios.post(`/oms/logistics/${docId}/resolve-issue/`, {
      remarks: remarks?.trim() || undefined,
    });
    message.success("Escalation resolved ✓");
    return res?.data as LogisticsOrderRow;
  } catch (error: any) {
    message.error(
      error?.response?.data?.detail || "Could not resolve the escalation"
    );
    return null;
  }
}

// ---------------------------------------------------------------------------
// Missing Orders — the Last Mile board's blind spot.
//
// The board shows TRUCKS, so it can only show work that exists. An allotment
// whose last leg never got a truck — stuck in a picklist error, waiting on a
// first mile that never arrived — has nothing on the board to be late, and the
// team that owes the customer the delivery never sees it. This is that list.
//
// Rooted on the delivery LEG (`id` is a stage id), not on a booking: there is
// no booking. That is the whole point.
// ---------------------------------------------------------------------------

// Why the last leg has no truck. `awaiting_booking` is the one ops can act on
// from the board itself — everything upstream has cleared. `error` / `held` win
// over the leg type, because a failure needs a named human and queue time does not.
export type MissingReasonCode =
  | "awaiting_booking"
  | "error"
  | "held"
  // Its own code, not folded into `held`: the leg is gated shut on purpose until the money lands.
  // Nothing is stuck and the delivery team owes it no action, so the board lifts these into their
  // own section rather than counting them as promises we dropped.
  | "payment_pending"
  | "po_pending"
  | "first_mile_pending"
  // Collected already — the first mile ran. What is outstanding is the receipt at our own dock.
  | "grn_pending"
  | "supplier_delivery_pending"
  | "transfer_pending"
  | "pick_pending"
  | "packing_pending"
  | "invoice_pending"
  | "service_pending"
  | "blocked"
  | "no_plan";

export interface MissingOrderRow {
  id: number; // the delivery leg's stage id
  operation_type: string;
  status: string;
  issue_state: string;
  due_at: string | null;
  sequence: number;

  sales_order_id: number | null;
  allocation_id: number | null;
  lead_id: string | null;
  po_number: string | null;
  client_name: string | null;
  supplier_name: string | null;

  // The promise, and the distance from it. `days_late === 0` is "due today, nothing
  // booked" — the case worth catching BEFORE the day runs out, not only after.
  target_delivery_date: string | null;
  expected_delivery_date: string | null;
  days_late: number | null;

  // Why nothing has left: the earliest leg of the chain still owed, and its state.
  // `blocking_detail` is the exact backend error / hold text — what says WHICH team to call.
  reason_code: MissingReasonCode;
  reason: string;
  blocking_stage_id: number | null;
  blocking_operation_type: string | null;
  blocking_status: string | null;
  blocking_detail: string | null;

  fulfilment_warehouse_id: number | null;
  fulfilment_warehouse_name: string | null;
  ship_city: string | null;
  ship_pincode: string | null;

  is_express: boolean;
  is_unloading: boolean;
  qty: string | number;
  item_count: number;
  cargo_weight_kg?: number | null;
  escalation: BookingEscalation | null;
  // Every escalation open on ANY still-outstanding leg of this allotment — what the row's ESCALATED
  // tag reads. Distinct from `escalation` above, which is only the DELIVERY leg's own (that being the
  // one the row's Resolve / Re-raise buttons act on).
  //
  // "Currently escalated" means: the escalation log's latest event for that leg is a RAISE, nobody has
  // resolved it since, AND the leg is still outstanding. The last clause matters — completing a leg
  // does not emit a resolve, so without it a stale escalation on a leg that finished weeks ago would
  // tag the row for ever.
  active_escalations: {
    operation_type: string;
    issue_type: string | null;
    count: number;
    raised_by: string | null;
    raised_at: string | null;
    // False → it sits upstream, i.e. it is another team's move, not this desk's.
    is_delivery_leg: boolean;
  }[];

  // --- the board row's own display fields --------------------------------
  // The section renders INSIDE the Last Mile board and through the SAME cells its other sections use
  // (`locationCell`, `floorLift`, the SO + PO cells), so a Missing Orders row has to answer the same
  // questions a booking row does. The server reads these off the delivery leg's frozen endpoint
  // snapshots — the very thing a booking's origin/destination is copied from — so the route shown
  // here is the route the truck would have had.
  po_numbers: string[];
  zoho_salesorder_number: string | null;
  origin_label: string | null;
  origin_address: string | null;
  origin_lat: number | null;
  origin_long: number | null;
  destination_label: string | null;
  destination_address: string | null;
  destination_lat: number | null;
  destination_long: number | null;
  client_contact: string | null;
  client_alternate_contact: string | null;
  delivery_floor: number | string | null;
  delivery_has_lift: boolean | null;
  is_transfer_chain: boolean;
  is_outstation: boolean;
}

export interface MissingOrdersResponse {
  count: number;
  limit: number;
  offset: number;
  on_date: string;
  days_back: number;
  // The server's row cap was hit — the count on screen is a floor, not a total.
  truncated: boolean;
  results: MissingOrderRow[];
}

export async function fetchMissingOrders(params: {
  // The day the window ENDS — promises on or before it. The board's day picker drives this, so the
  // section narrows with the control above it rather than ignoring it.
  on_date?: string;
  days_back?: number;
  fulfilment_warehouse_id?: number;
  express?: boolean;
  city?: string;
  search?: string;
}): Promise<MissingOrdersResponse | undefined> {
  try {
    const res = await axios.get("/oms/logistics/missing-orders/", {
      params: {
        on_date: params.on_date || undefined,
        // 0 is meaningful ("no floor — show me everything"), so it must survive the
        // falsy check a plain `|| undefined` would apply.
        days_back: params.days_back ?? undefined,
        fulfilment_warehouse_id: params.fulfilment_warehouse_id || undefined,
        express: params.express ? "true" : undefined,
        city: params.city || undefined,
        search: params.search || undefined,
        limit: 200,
      },
    });
    return res?.data as MissingOrdersResponse;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not load missing orders");
    return undefined;
  }
}
